import clsx from 'clsx';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import PageAuthenticationGuard from '@components/atoms/authentication/page-authentication-guard/page-authentication-guard';
import Button from '@components/atoms/button/button';
import Icon from '@components/atoms/icon/icon';
import Spinner from '@components/atoms/spinner/spinner';
import Head from '@components/atoms/head/head';
import { useAnalytics } from '@context/analytics.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import extractFromContext from '@helpers/utils/extract-from-context';
import { defaultMetas } from '@constants/metas/default';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import { ListingContext, getIsJewelry } from '@context/listing.context';
import { usePreferences } from '@context/preferences.context';
import SellLayout from '@layouts/sell-layout';
import DefaultLayout from '@layouts/default-layout';
import useUser from '@hooks/user/use-user';
import Link from '@components/atoms/link/link';
import { getAppInitialProps } from '@helpers/initial-props';
import { BrandSearch } from '@components/brand-search/brand-search';
import { UNSIGNED_BRAND_ID } from '@components/brand-search/brand-search-presenter';
import { UniverseSelector } from '@components/universe-selector/universe-selector';
import { CategorySelector } from '@components/category-selector/category-selector';
import { Category } from '@interfaces/models/deposit';
import sanitizer from '@helpers/sanitizer';
import styles from './index.module.scss';

const SellClothesOnlinePage: NextPageWithLayout = () => {
  const { t } = useTranslation('deposit-form');
  const { updateDataLayer } = useAnalytics();
  const { language } = usePreferences();
  const { sendAnalyticEvent } = useAnalyticEvents('listingForm');
  const { isAuthenticated } = useUser();
  const {
    universes,
    pendingAction,
    existingDraftCount,
    listingDraftPayload,
    setListingDraftAttribute,
    createListingDraft,
    setBrandSearchTerm,
    brandsByFilter,
    brandSearchTerm,
    isJewelry,
  } = useContextSelector(ListingContext, (state) => state);

  const pageMetas = defaultMetas[language];
  const redirectionUrl = useRef<string | null>(`/journal/start-selling/`);
  const [preductId, setPreductId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDraft, setLoadingDraft] = useState<boolean>(false);
  const [isBanned, setIsBanned] = useState<boolean>(false);
  const [isDraftSaved, setIsDraftSaved] = useState<boolean>(null);
  const handleDraftLoader = () => setLoadingDraft(true);

  const listingFormShouldShow = isAuthenticated;
  const disabled =
    !(listingDraftPayload.universe && listingDraftPayload.category && listingDraftPayload.brand) || isBanned;

  const categories: Category[] = useMemo(() => {
    const data = universes?.filter((i) => i.id === listingDraftPayload.universe)[0]?.categories;
    return data || [];
  }, [listingDraftPayload.universe, universes]);

  const handleChangeUniverse = (e) => {
    setListingDraftAttribute({
      universe: e.target.value,
      category: '',
    });
    sendAnalyticEvent('submit_universe', {
      eventLabel: e.target.dataset?.inputTextValue,
    });
  };

  const handleChangeCategory = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // if brand is already selected unsigned and category is changed to jewelry, reset brand
    if (listingDraftPayload.brand === UNSIGNED_BRAND_ID && getIsJewelry(e.target.value, universes)) {
      setBrandSearchTerm('');
      setListingDraftAttribute({ brand: '' });
    }
    setListingDraftAttribute({ category: e.target.value });
    sendAnalyticEvent('submit_category', {
      eventLabel: e.target.selectedOptions[0]?.innerText,
    });
  };

  const handleSubmitForm = async () => {
    sendAnalyticEvent('submit_brand_next');

    if (isAuthenticated) {
      setLoading(true);
      const { draftId, error } = await createListingDraft('listing_form');

      if (error) {
        setError(error);
        setLoading(false);
      }

      if (draftId) {
        sendAnalyticEvent('submit_draft_creation', {
          eventLabel: 'organic',
          preduct_id: draftId,
        });
        setPreductId(draftId);
      }
    }
  };

  /**
   * This effect fires an analytics event when the user is viewing the listing form.
   */
  useEffect(() => {
    updateDataLayer({
      env_template: 'sell_an_item',
      pageUrl: '/submit-an-item',
      screen_category: 'listing_form',
      screen_subcategory: 'submit_an_item',
      screen_name: '/submit_an_item',
    });
  }, [updateDataLayer]);

  useEffect(() => {
    const { search, pathname } = window.location;
    const query = new URLSearchParams(search);
    const foundDraft = query.get('draft_saved') === 'true';

    setIsDraftSaved(foundDraft);
    query.delete('draft_saved');
    const updatedUrl = query.toString() ? `${pathname}?${query.toString()}` : pathname;
    window.history.pushState(null, '', updatedUrl);
  }, []);

  // This effect handles the redirect once a preduct id is created and sends the analytic event.
  useEffect(() => {
    if (preductId && typeof window !== 'undefined') {
      window.location.href = `${window.location.origin}${t('DEPOSIT_FORM.CONTINUE.URL')}?id=${preductId}`;
    }
  }, [preductId]);

  return (
    <>
      <Head metadata={pageMetas} />
      <PageAuthenticationGuard redirectionUrl={redirectionUrl.current}>
        <main className={clsx(styles.depositPage)}>
          <div className={clsx(styles.depositForm, 'innerContainer')}>
            {isDraftSaved && (
              <div className={clsx(styles.depositForm__savedBanner)}>
                <Icon
                  name="success-24"
                  className={clsx(styles.depositForm__savedBanner__icon)}
                />
                <p>{t('DEPOSIT_FORM.DRAFT_CONFIRMATION_BANNER')}</p>
              </div>
            )}
            {pendingAction || loading || !isAuthenticated ? (
              <div className={clsx(styles.depositForm__container, styles.depositForm__loadingFeature_container)}>
                <Spinner />
              </div>
            ) : (
              <>
                {!listingFormShouldShow ? (
                  <div className={clsx(styles.depositForm__container, styles.depositForm__loadingFeature_container)}>
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <section className={clsx(styles.depositForm__header)}>
                      <h1 className={clsx(styles.depositForm__title)}>{t('DEPOSIT_FORM.HEADER.TITLE')}</h1>
                      <p className={clsx(styles.depositForm__intro)}>{t('DEPOSIT_FORM.HEADER.INTRO')}</p>
                    </section>
                    <section className={clsx(styles.depositForm__content)}>
                      {existingDraftCount > 0 && (
                        <div
                          className={styles.depositForm__reminder}
                          data-cy="complete-submission"
                        >
                          <p className={styles.depositForm__reminder__text}>
                            {existingDraftCount > 1
                              ? t('DEPOSIT_FORM.ALREADY_STARTED_DRAFTS_PLURAL', {
                                  draftNb: existingDraftCount,
                                })
                              : t('DEPOSIT_FORM.ALREADY_STARTED_DRAFTS_SINGLE')}
                          </p>
                          <div className={styles.depositForm__reminder__linkContainer}>
                            {loadingDraft ? (
                              <Spinner className={styles.depositForm__reminder__loading} />
                            ) : (
                              <Link
                                href="/sell-clothes-online/product-drafts"
                                className={clsx(
                                  styles.depositForm__reminder__link,
                                  'vc-btn vc-btn--large vc-btn--full vc-btn--notfull--md-up',
                                )}
                                onClick={handleDraftLoader}
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizer(
                                      existingDraftCount > 1
                                        ? t('DEPOSIT_FORM.COMPLETE_DRAFTS_PLURAL')
                                        : t('DEPOSIT_FORM.COMPLETE_DRAFT_SINGLE'),
                                    ),
                                  }}
                                ></span>
                              </Link>
                            )}
                          </div>
                        </div>
                      )}
                      {loading ? (
                        <div className={clsx(styles.depositForm__container)}>
                          <Spinner />
                        </div>
                      ) : (
                        <div
                          id="vc-preduct-add-form"
                          className={clsx(styles.depositForm__form)}
                        >
                          <fieldset className={clsx(styles.depositForm__form__subsection)}>
                            <p className={clsx(styles.depositForm__form__subtitle, 'vc-text-m')}>
                              {t('DEPOSIT_FORM.CATEGORIES')}
                            </p>
                            <UniverseSelector
                              universes={universes}
                              listingDraftPayload={listingDraftPayload}
                              handleChangeUniverse={handleChangeUniverse}
                            />
                          </fieldset>
                          <fieldset className={clsx(styles.depositForm__form__subsection)}>
                            <label
                              htmlFor="preductAddCategory"
                              className={clsx(styles.depositForm__form__subtitle, 'vc-text-m')}
                            >
                              {t('DEPOSIT_FORM.PRODUCT')}
                            </label>
                            <CategorySelector
                              categories={categories}
                              listingDraftPayload={listingDraftPayload}
                              handleChangeCategory={handleChangeCategory}
                            />
                          </fieldset>
                          <fieldset className={clsx(styles.depositForm__form__subsection)}>
                            <label
                              htmlFor="depositForm__form__brands-input"
                              className={clsx(styles.depositForm__form__subtitle, 'vc-text-m')}
                            >
                              {t('DEPOSIT_FORM.BRAND')}
                            </label>
                            <BrandSearch
                              isJewelry={isJewelry}
                              universes={universes}
                              isBanned={isBanned}
                              setIsBanned={setIsBanned}
                              listingDraftPayload={listingDraftPayload}
                              setListingDraftAttribute={setListingDraftAttribute}
                              brandsByFilter={brandsByFilter}
                              brandSearchTerm={brandSearchTerm}
                              setBrandSearchTerm={setBrandSearchTerm}
                            />
                          </fieldset>
                          <div className={clsx(styles.depositForm__form__submit)}>
                            <Button
                              type="submit"
                              id="vc-preduct-add-submit"
                              variant="primary"
                              size="large"
                              fullWidth
                              notFullWidthQuery="md-up"
                              disabled={disabled}
                              onClick={handleSubmitForm}
                            >
                              {t('DEPOSIT_FORM.COMPLETE_DRAFTS_PLURAL')}
                            </Button>
                          </div>
                          {error && (
                            <div className={clsx(styles.depositForm__form__error)}>
                              <span className="error-msg">{error}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </section>
                    <ul className={clsx(styles.depositForm__reassurance)}>
                      <li className={clsx(styles.depositForm__reassurance__item)}>
                        <Icon
                          className={clsx(styles.depositForm__reassurance__icon)}
                          name="label-large"
                        />
                        <div className={clsx(styles.depositForm__reassurance__title)}>
                          {t('DEPOSIT_FORM.REASSURANCE.CONCEPT.TITLE')}
                        </div>
                        <p className={clsx(styles.depositForm__reassurance__text)}>
                          {t('DEPOSIT_FORM.REASSURANCE.CONCEPT.TEXT')}
                        </p>
                        <a
                          className={clsx(styles.depositForm__reassurance__link)}
                          href={t('DEPOSIT_FORM.REASSURANCE.CONCEPT.LINK_URL')}
                        >
                          {t('DEPOSIT_FORM.REASSURANCE.CONCEPT.LINK')}
                        </a>
                      </li>
                      <li className={clsx(styles.depositForm__reassurance__item)}>
                        <Icon
                          className={clsx(styles.depositForm__reassurance__icon)}
                          name="me-large"
                        />
                        <div className={clsx(styles.depositForm__reassurance__title)}>
                          {t('DEPOSIT_FORM.REASSURANCE.SELLER_PRO.TITLE')}
                        </div>
                        <p className={clsx(styles.depositForm__reassurance__text)}>
                          {t('DEPOSIT_FORM.REASSURANCE.SELLER_PRO.TEXT')}
                        </p>
                        <a
                          className={clsx(styles.depositForm__reassurance__link)}
                          href={t('DEPOSIT_FORM.REASSURANCE.SELLER_PRO.LINK_URL')}
                        >
                          {t('DEPOSIT_FORM.REASSURANCE.SELLER_PRO.LINK')}
                        </a>
                      </li>
                    </ul>
                    {language === 'fr' && (
                      <p className={clsx(styles.depositForm__warningMsg)}>
                        Si vous agissez à titre professionnel, vous devez créer un compte professionnel en adressant
                        votre demande à contactpro@vestiairecollective.com. A défaut, en application de l’
                        <a
                          href="https://www.legifrance.gouv.fr/affichCodeArticle.do?cidTexte=LEGITEXT000006069565&amp;idArticle=LEGIARTI000006292182"
                          target="_blank"
                          rel="noreferrer noopener"
                          className={clsx(styles.depositForm__warningMsg__link)}
                        >
                          article L 132-2 du Code de la consommation
                        </a>{' '}
                        qui sanctionne les pratiques commerciales trompeuses, vous encourez notamment une peine
                        d’emprisonnement de 2 ans et une amende de 300 000 euros.
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {loading || (!isAuthenticated && <div className={clsx(styles.depositForm__pageOverlay)} />)}
        </main>
      </PageAuthenticationGuard>
    </>
  );
};

SellClothesOnlinePage.Layout = [DefaultLayout, SellLayout];
SellClothesOnlinePage.PageType = 'SellClothesOnlinePage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, ['default-layout', 'deposit-form', 'common']);
  return {
    props: {
      ...initialProps,
      ...translations,
    },
  };
};

export default SellClothesOnlinePage;
